import { type InputHTMLAttributes, type FC } from 'react'
import cn from '@/utils/classNames'
import styles from './Input.module.scss'

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /**
   * Optional label for the input
   */
  label?: string

  /**
   * If true, applies error styling
   */
  hasError?: boolean

  /**
   * Error message if validation fails
   */
  errorMessage?: string

  /**
   * Input style variant
   * - 'default': white background
   * - 'transparent': linear with white border
   */
  variant?: 'default' | 'transparent'
}

/**
 * A reusable input component with built-in label and styling.
 *
 * @component
 * @example
 * <Input
 *   id="email"
 *   name="email"
 *   type="email"
 *   required
 *   defaultValue="test.user@1ocean.org"
 *   placeholder="Email"
 * />
 */
const Input: FC<InputProps> = ({ label, className, hasError, errorMessage, ...rest }) => {
  return (
    <div className={styles.wrapper}>
      {label && (
        <label htmlFor={rest.id} className={styles.label}>
          {label}
        </label>
      )}
      <input
        className={cn(
          styles.input,
          hasError && styles.error,
          rest.variant === 'transparent' && styles.transparent,
          className
        )}
        {...rest}
      />
      {hasError && errorMessage && (
        <p className={styles.errorMessage}>{errorMessage}</p>
      )}
    </div>
  )
}

export default Input
